import React, { type ReactNode, useMemo } from 'react'
import { DepositFlow } from '@mobi/account/Areas/Deposit/typings/types'
import { type HostApi, HostProvider } from '@mobi/account/Common/HostContext'
import { isReactNativeApp, sendToNative } from '@mobi/web-native-comms/web'
import { postLogout } from '@core/Areas/Login/helpers'
import { ClearInsufficientFundsForBet } from '@core/Areas/Quickbet/signals'
import { keys } from '@core/Services/Analytics/keys'
import { trackEvent } from '@core/Services/Analytics/track'
import { getFeatureValue } from '@core/State/FeatureFlags/helpers/getFeatureValue'
import { appHistory } from '@core/Utils/routing/history'

type Params = {
  history: typeof appHistory
}

const createHostApi = ({ history }: Params): HostApi => ({
  features: {
    hasFeature: featureKey => {
      return new Promise(resolve => {
        const val = getFeatureValue(featureKey)
        resolve(val as PromiseLike<boolean>)
      })
    },
  },
  events: {
    onDepositSuccess: (flow: DepositFlow, amount?: number) => {
      if (!amount) return
      if (flow === 'quick-deposit') {
        ClearInsufficientFundsForBet()
      }
      trackEvent(keys.moneyDeposited, { amount })
    },
    findTab: () => {
      if (isReactNativeApp) {
        sendToNative('REQUEST_NATIVE_NAVIGATION', { location: 'TAB Locator' })
      } else {
        history.push('/tablocator')
      }
    },
  },
  channel: 'mobile',
  logout: () => postLogout(),
})

type Props = {
  children: MaybeArray<ReactNode>
}

export const HostContextMobile = ({ children }: Props): JSX.Element => {
  // TODO: Remove this when CreditCardForm is more resilient
  // This is a memo to ensure the `analytics` object (see above) remains the same
  // as that is checked when adding a card with Braintree. Not having this can
  // cause Braintree's hosted fields to crash when anything in the parent
  // has changed.
  const api = useMemo(
    () =>
      createHostApi({
        history: appHistory,
      }),
    []
  )

  return <HostProvider api={api}>{children}</HostProvider>
}
